import { Button, InputNumber, Space, Table } from '@arco-design/web-react'
import { IconDelete, IconPlus } from '@arco-design/web-react/icon'

interface DiscountRow {
  amount: number
  discount: number
}

function parseDiscounts(raw: string): DiscountRow[] {
  if (!raw?.trim()) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return []
    return Object.entries(parsed as Record<string, unknown>)
      .map(([amount, discount]) => ({
        amount: Number(amount),
        discount: Number(discount),
      }))
      .filter((r) => Number.isFinite(r.amount) && Number.isFinite(r.discount))
      .sort((a, b) => a.amount - b.amount)
  } catch {
    return []
  }
}

function toJson(rows: DiscountRow[]): string {
  const obj: Record<string, number> = {}
  rows.forEach((r) => {
    if (r.amount > 0 && r.discount > 0 && r.discount <= 1) {
      obj[String(r.amount)] = r.discount
    }
  })
  return JSON.stringify(obj)
}

interface Props {
  value: string
  onChange: (json: string) => void
}

/** 可视化编辑 payment_setting.amount_discount */
export default function AmountDiscountEditor({ value, onChange }: Props) {
  const rows = parseDiscounts(value)

  const update = (next: DiscountRow[]) => onChange(toJson(next))

  const columns = [
    {
      title: '充值金额（USD）',
      render: (_: unknown, _row: DiscountRow, index: number) => (
        <InputNumber
          min={1}
          style={{ width: '100%' }}
          value={rows[index]?.amount}
          onChange={(v) => {
            const next = [...rows]
            next[index] = { ...next[index], amount: v ?? 0 }
            update(next)
          }}
        />
      ),
    },
    {
      title: '折扣系数（0–1）',
      render: (_: unknown, _row: DiscountRow, index: number) => (
        <InputNumber
          min={0.01}
          max={1}
          step={0.01}
          style={{ width: '100%' }}
          value={rows[index]?.discount}
          onChange={(v) => {
            const next = [...rows]
            next[index] = { ...next[index], discount: v ?? 1 }
            update(next)
          }}
        />
      ),
    },
    {
      title: '',
      width: 56,
      render: (_: unknown, _row: DiscountRow, index: number) => (
        <Button
          type="text"
          status="danger"
          icon={<IconDelete />}
          onClick={() => update(rows.filter((_, i) => i !== index))}
        />
      ),
    },
  ]

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div className="settings-table-scroll">
        <Table
          columns={columns}
          data={rows.length ? rows : [{ amount: 100, discount: 0.95 }]}
          pagination={false}
          rowKey={(record) => `${record.amount}-${record.discount}`}
        />
      </div>
      <Button type="dashed" icon={<IconPlus />} onClick={() => update([...rows, { amount: 0, discount: 1 }])}>
        添加折扣档位
      </Button>
    </Space>
  )
}

export function validateAmountDiscount(raw: string): string | null {
  if (!raw?.trim()) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return '必须是 JSON 对象，键为金额、值为折扣'
    }
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      const amount = Number(k)
      const discount = Number(v)
      if (!Number.isFinite(amount) || amount <= 0) return `无效金额键：${k}`
      if (!Number.isFinite(discount) || discount <= 0 || discount > 1) {
        return `金额 ${k} 的折扣须在 (0, 1] 之间`
      }
    }
    return null
  } catch {
    return '不是合法的 JSON 对象'
  }
}
